import React, { useState, useEffect } from "react";
import SEO from "../seo";
import { STAFF_ACCOUNTS } from "../constant_data";
export default function AddStaffPermissions() {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "invite-steps",
        "roles-permissions",
        "quick-tips",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Add Staff and Set Permissions – xPAGE Help Center"
        description="Learn how to add staff to your xPage store and manage permissions. Invite members, assign roles, and control access securely in your admin dashboard."
        keywords="xPAGE, staff permissions, team roles, invite staff, eCommerce team management, xPage help, online store admin"
        canonical="https://help.xpage.ai/help/landing-page-setup/Add%20Staff%20and%20Set%20Permissions"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          Add Staff and Set Permissions
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          With xPage, you can build a trusted team by inviting staff members to
          help manage your store while controlling their access via roles and
          permissions.
        </p>

        {/* Overview */}
        <section id="overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Overview
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm mb-3 text-systemTitle">
              xPage Admin enables you to:
            </p>
            <ul className="space-y-1 text-sm list-disc list-inside text-system600">
              <li>Invite team members with email-based invitations</li>
              <li>Assign roles to determine access levels</li>
              <li>Manage existing staff, update or remove permissions</li>
              <li>Ensure secure, scoped access to your store operations</li>
            </ul>
          </div>
        </section>

        {/* Invite Steps */}
        <section id="invite-steps" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Invite a Staff Member
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemTitle mb-3">
              To bring a new team member onboard:
            </p>

            <ol className="list-decimal list-inside text-systemTitle space-y-1 text-sm">
              <li className="mb-2">Log in to your xPage Admin dashboard.</li>
              <li className="mb-2">
                Navigate to <strong>Settings → User & Permissions</strong>.
              </li>
              <li className="mb-2">
                Click <strong>Invite Staff</strong>.
              </li>
              <li className="mb-2">
                Enter the staff member's email and assign access.
              </li>
              <li className="mb-2">
                Click <strong>Send Invitation</strong>. The invitee will receive
                an email to create their login credentials.
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span> Use unique emails
                per staff member to ensure secure login tracking.
              </p>
            </div>
          </div>
        </section>

        {/* Roles and Permissions */}
        <section id="roles-permissions" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Staff Roles and Permissions
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { plan: "Starter Plan", members: STAFF_ACCOUNTS.STARTER },
                { plan: "Growth Plan", members: STAFF_ACCOUNTS.GROWTH },
                { plan: "Pro Plan", members: STAFF_ACCOUNTS.PRO },
                { plan: "Enterprise Plan", members: STAFF_ACCOUNTS.ENTERPRISE },
              ].map((item) => (
                <div
                  key={item.plan}
                  className="bg-systemBackground border border-systemBorder rounded-xl p-4 shadow-sm hover:shadow-md transition"
                >
                  <h3 className="text-lg font-semibold text-systemTitle mb-1">
                    {item.plan}
                  </h3>
                  <p className="text-sm text-systemLabel">{item.members}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quick Tips */}
        <section id="quick-tips" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Quick Tips
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="space-y-1 text-sm text-system600">
              <li className="mb-2">
                <strong>Audit regularly:</strong> Re-visit roles as team
                responsibilities change.
              </li>
              <li className="mb-2">
                <strong>Immediate removal:</strong> If a staff member leaves
                your team, revoke their access without delay.
              </li>
            </ul>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#invite-steps"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "invite-steps"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Invite Steps
              </a>
            </li>
            <li>
              <a
                href="#roles-permissions"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "roles-permissions"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Roles & Permissions
              </a>
            </li>
            <li>
              <a
                href="#quick-tips"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "quick-tips"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Quick Tips
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
